import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Page from '../components/Page';

const apiURL = `${process.env.REACT_APP_SERVICE_BASE_URL}/admin/statistic`;

const EMPTY_ACCESS = {
  uniqueUsers: 0,
  sessions: 0,
  ios: 0,
  android: 0,
  other: 0,
};

const EMPTY_STATISTICS = {
  users: { total: 0, active: 0 },
  access: { timezone: 'Asia/Ho_Chi_Minh', today: EMPTY_ACCESS, daily: [] },
  books: { monthly: { total: 0 } },
  revenue: { monthly: { total: 0 } },
  orders: { monthly: { total: 0 } },
};

const numberFormatter = new Intl.NumberFormat('vi-VN');

const formatNumber = value => numberFormatter.format(Number(value) || 0);

const formatDate = value => {
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
};

function MetricCard({ title, value, description, color }) {
  return (
    <Card sx={{ height: '100%', bgcolor: color, boxShadow: 'none' }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h3">{formatNumber(value)}</Typography>
        <Typography variant="subtitle1" sx={{ mt: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
}

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.number,
  description: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

MetricCard.defaultProps = {
  value: 0,
};

export default function DashboardApp() {
  const [statistics, setStatistics] = useState(EMPTY_STATISTICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');

    axios
      .get(apiURL, { headers: { token: storedUser.token } })
      .then(response => {
        if (!active) return;
        const data = response.data?.data || {};
        setStatistics({
          ...EMPTY_STATISTICS,
          ...data,
          users: { ...EMPTY_STATISTICS.users, ...data.users },
          access: {
            ...EMPTY_STATISTICS.access,
            ...data.access,
            today: { ...EMPTY_ACCESS, ...data.access?.today },
            daily: data.access?.daily || [],
          },
        });
      })
      .catch(requestError => {
        if (!active) return;
        setError(requestError.response?.data?.message || 'Không tải được số liệu dashboard.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const { access, users } = statistics;
  const dailyRows = [...access.daily].reverse();

  return (
    <Page title="Dashboard | Admin">
      <Container maxWidth="xl">
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">Tổng quan truy cập</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Một người được tính theo một installation duy nhất mỗi ngày, theo múi giờ Việt Nam (UTC+7).
          </Typography>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 10 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Người truy cập hôm nay"
                  value={access.today.uniqueUsers}
                  description="Tổng installation duy nhất"
                  color="primary.lighter"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Thiết bị iOS"
                  value={access.today.ios}
                  description="iPhone/iPad truy cập hôm nay"
                  color="info.lighter"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Thiết bị Android"
                  value={access.today.android}
                  description="Android truy cập hôm nay"
                  color="success.lighter"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Lượt mở ứng dụng"
                  value={access.today.sessions}
                  description="Bao gồm nhiều phiên của cùng một người"
                  color="warning.lighter"
                />
              </Grid>
            </Grid>

            <Card sx={{ mt: 4 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Truy cập 7 ngày gần nhất
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ngày</TableCell>
                        <TableCell align="right">Người truy cập</TableCell>
                        <TableCell align="right">iOS</TableCell>
                        <TableCell align="right">Android</TableCell>
                        <TableCell align="right">Lượt mở app</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailyRows.map(row => (
                        <TableRow key={row.date} hover>
                          <TableCell>{formatDate(row.date)}</TableCell>
                          <TableCell align="right">{formatNumber(row.uniqueUsers)}</TableCell>
                          <TableCell align="right">{formatNumber(row.ios)}</TableCell>
                          <TableCell align="right">{formatNumber(row.android)}</TableCell>
                          <TableCell align="right">{formatNumber(row.sessions)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            <Typography variant="h5" sx={{ mt: 5, mb: 3 }}>
              Tài khoản và kinh doanh
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Tổng thành viên"
                  value={users.total}
                  description="Tài khoản đã đăng ký"
                  color="grey.200"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Thành viên mua hàng"
                  value={users.active}
                  description="Người dùng có đơn hàng"
                  color="grey.200"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Sách bán trong tháng"
                  value={statistics.books?.monthly?.total}
                  description="Sách giấy và sách điện tử"
                  color="grey.200"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Đơn hàng trong tháng"
                  value={statistics.orders?.monthly?.total}
                  description="Tổng đơn hàng tháng hiện tại"
                  color="grey.200"
                />
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </Page>
  );
}
